import { createQueryKeys } from '@lukemorales/query-key-factory';
import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { ParcelServiceApi } from 'api';
import type { ParcelService } from 'api/methods/ParcelServiceApi';

/**
 * [ParcelService] Query key 생성
 * --
 */
export const parcelServiceQueryKeys = createQueryKeys('parcelservice', {
  getParcelService: (
    idx: number,
    params?: { [key: string]: string | number }
  ) => [idx, params],
  getParcelServices: (params?: { [key: string]: string | number }) => [params],
});

/**
 * [ParcelService] 배송/정기배송 전체 조회
 * --
 */
export const useGetParcelServices = (
  params?: {
    [key: string]: string | number;
  },
  options?: UseQueryOptions<any, any>
) => {
  const data = useQuery({
    queryKey: parcelServiceQueryKeys.getParcelServices(params).queryKey,
    queryFn: () => ParcelServiceApi.getParcelServices(params),
    ...options,
  });
  return data;
};

/**
 * [ParcelService] 배송/정기배송 상세 조회
 * --
 */
export const useGetParcelService = (
  idx: number,
  params?: { [key: string]: string | number }
) => {
  const data = useQuery({
    queryKey: parcelServiceQueryKeys.getParcelService(idx, params).queryKey,
    queryFn: () => ParcelServiceApi.getParcelService(idx, params),
  });

  return data;
};

/**
 * [ParcelService] 배송/정기배송 생성
 * --
 */
export const useCreateParcelService = () => {
  const { mutateAsync: createParcelService, ...result } = useMutation({
    mutationFn: (variables: ParcelService) =>
      ParcelServiceApi.createParcelService(variables),
  });

  return { createParcelService, ...result };
};

/**
 * [ParcelService] 배송/정기배송 수정
 * --
 */
export const useUpdateParcelService = () => {
  const { mutateAsync: updateParcelService, ...result } = useMutation({
    mutationFn: (variables: ParcelService) =>
      ParcelServiceApi.updateParcelService(variables),
  });

  return { updateParcelService, ...result };
};

/**
 * [ParcelService] 배송/정기배송 삭제
 * --
 */
export const useDeleteParcelService = () => {
  const { mutateAsync: deleteParcelService, ...result } = useMutation({
    mutationFn: (variables: number) =>
      ParcelServiceApi.deleteParcelService(variables),
  });

  return { deleteParcelService, ...result };
};

/**
 * [ParcelService] 배송/정기배송 Bulk 생성
 * --
 */
export const useBulkCreateParcelService = () => {
  const { mutateAsync: bulkCreateParcelService, ...result } = useMutation({
    mutationFn: (variables: ParcelService[]) =>
      ParcelServiceApi.bulkCreateParcelService(variables),
  });

  return { bulkCreateParcelService, ...result };
};

/**
 * [ParcelService] 배송/정기배송 Bulk 수정
 * --
 */
export const useBulkUpdateParcelService = () => {
  const { mutateAsync: bulkUpdateParcelService, ...result } = useMutation({
    mutationFn: (variables: ParcelService[]) =>
      ParcelServiceApi.bulkUpdateParcelService(variables),
  });

  return { bulkUpdateParcelService, ...result };
};

/**
 * [ParcelService] 배송/정기배송 삭제
 * --
 */
export const useBulkDeleteParcelService = () => {
  const { mutateAsync: bulkDeleteParcelService, ...result } = useMutation({
    mutationFn: (variables: ParcelService[]) =>
      ParcelServiceApi.bulkDeleteParcelService(variables),
  });

  return { bulkDeleteParcelService, ...result };
};

/**
 * [ParcelService] 배송/정기배송 상태 수정
 * --
 */
export const useUpdateParcelServiceStatus = () => {
  const { mutateAsync: updateParcelServiceStatus, ...result } = useMutation({
    mutationFn: (variables: ParcelService) =>
      ParcelServiceApi.updateParcelServiceStatus(variables),
  });

  return { updateParcelServiceStatus, ...result };
};

/**
 * [ParcelService] 배송/정기배송 구매확정
 * --
 */
export const useConfirmParcelServiceStatus = () => {
  const { mutateAsync: confirmParcelServiceStatus, ...result } = useMutation({
    mutationFn: (variables: ParcelService) =>
      ParcelServiceApi.confirmParcelServiceStatus(variables),
  });

  return { confirmParcelServiceStatus, ...result };
};
